// Phase 83s — User-level notification bell hook + WebSocket subscription.
// REST: GET /api/notifications + POST /api/notifications/{id}/read + /read-all.
// v17.10 — WS migrated to WS Multiplex Hub (`wsHub.js`) — 1 socket duy nhất
// cho toàn bộ 6 hook. Server route per-user_id (auth ticket) → chỉ user
// tương ứng nhận thông báo.
//
// On every WS push of type "notification" we:
//   (1) Invalidate the notification list query so the bell + drawer re-render.
//   (2) Invalidate `["athlete","registrations"]` because most user-facing
//       notifications (registration.approved / payment.paid) imply that the
//       VĐV's registrations + matches widgets must reflect the new state.
//   (3) Play `dingNotification()` (no-op when user has muted in Settings).
//
// Returns: { items, unreadCount, isLoading, markRead(id), markAllRead, refetch }
import { asset } from "../lib/asset";
import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { subscribe as wsSubscribe } from "../lib/wsHub";
import { dingNotification } from "../lib/audioCues";

// Phase 83y.50 — Notification types that should also fire (a) a sonner toast
// when the user is online, and (b) a browser Notification (if permission was
// granted). All other types stay in the bell drawer only.
const HIGH_PRIORITY_TYPES = new Set(["bracket.ready", "schedule.published"]);

function _maybeShowBrowserNotification(payload) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  try {
    const n = new Notification(payload.title || "PickLeague", {
      body: payload.body || "",
      icon: asset("/home-assets/tournament-logos/logo-superstars.webp"),
      tag: payload.type || "pickleague",
    });
    n.onclick = () => {
      try { window.focus(); } catch { /* noop */ }
      if (payload.link) window.location.href = payload.link;
      try { n.close(); } catch { /* noop */ }
    };
  } catch { /* noop */ }
}

export default function useUserNotifications({ enabled = true, userId = null } = {}) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  // Phase 83y.50 — Ask for browser-notification permission on first mount
  // for an authenticated user. We do this lazily and silently — failure is
  // a no-op so the rest of the app is unaffected.
  useEffect(() => {
    if (!enabled || !userId) return;
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "default") {
      // Defer slightly so we don't interrupt the initial app render with a
      // browser prompt.
      const t = setTimeout(() => {
        try { Notification.requestPermission().catch(() => {}); }
        catch { /* noop */ }
      }, 4000);
      return () => clearTimeout(t);
    }
  }, [enabled, userId]);

  const query = useQuery({
    queryKey: ["notifications", "list"],
    queryFn: () => api.get("/notifications", { params: { limit: 30 } }).then((r) => r.data),
    enabled,
    staleTime: 60_000,
    retry: 0,
  });

  const markReadMut = useMutation({
    mutationFn: (id) => api.post(`/notifications/${id}/read`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications", "list"] }),
  });

  const markAllReadMut = useMutation({
    mutationFn: () => api.post("/notifications/read-all").then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications", "list"] }),
  });

  // WS subscription — via wsHub singleton.
  useEffect(() => {
    if (!enabled || !userId) return undefined;

    const onPush = (payload) => {
      // Always re-fetch list & unread count
      qc.invalidateQueries({ queryKey: ["notifications", "list"] });
      // Phase 83y.15 — Audible "ting". `dingNotification` itself checks
      // `isAudioEnabled()` so mute-from-Settings is respected automatically.
      try { dingNotification(); } catch { /* noop */ }
      const type = payload?.data?.type || "";
      const data = payload?.data || {};

      // Phase 83y.50 — High-priority bracket / schedule pushes: fire a sonner
      // toast + browser Notification so the VĐV doesn't miss them.
      if (HIGH_PRIORITY_TYPES.has(type)) {
        const link = data.link;
        toast.custom(
          (id) => (
            <div
              role="button"
              tabIndex={0}
              onClick={() => {
                toast.dismiss(id);
                if (link) navigate(link);
              }}
              className="rounded-2xl px-4 py-3 flex items-center gap-3 min-w-[300px] max-w-[400px] cursor-pointer"
              style={{
                background: "linear-gradient(120deg, rgba(255,215,0,0.18) 0%, rgba(11,18,32,0.96) 50%, rgba(52,216,255,0.18) 100%)",
                border: "1px solid rgba(255,215,0,0.45)",
                boxShadow: "0 12px 40px rgba(0,0,0,0.55), 0 0 22px rgba(255,179,0,0.22)",
              }}
              data-testid={`toast-${type.replace(".", "-")}`}
            >
              <span className="text-[18px]">{type === "bracket.ready" ? "🎯" : "🗓️"}</span>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-black text-white leading-tight">{data.title || "Thông báo mới"}</div>
                {data.body && (
                  <div className="text-[11px] text-white/70 mt-0.5 line-clamp-2">{data.body}</div>
                )}
                {link && <div className="text-[10px] text-[#FFD700] font-bold mt-1">Bấm để xem →</div>}
              </div>
            </div>
          ),
          { duration: 6000, unstyled: true },
        );
        _maybeShowBrowserNotification({
          title: data.title,
          body: data.body,
          type,
          link,
        });
      }

      if (type.startsWith("registration.")) {
        qc.invalidateQueries({ queryKey: ["athlete", "registrations"] });
        qc.invalidateQueries({ queryKey: ["athlete", "matches"] });
        qc.invalidateQueries({
          predicate: (q) => {
            const k = q.queryKey;
            return Array.isArray(k) && k[0] === "public" && k[1] === "athletes";
          },
        });
        qc.invalidateQueries({ queryKey: ["public", "tournaments", "list"] });
        qc.invalidateQueries({
          predicate: (q) => {
            const k = q.queryKey;
            return (
              Array.isArray(k) &&
              k[0] === "public" &&
              k[1] === "tournament" &&
              (k[k.length - 1] === "header-stats" || k[k.length - 1] === "detail")
            );
          },
        });
      }
      if (type === "rating.verified") {
        qc.invalidateQueries({
          predicate: (q) => {
            const k = q.queryKey;
            return Array.isArray(k) && (
              (k[0] === "admin" && k[1] === "registrations") ||
              (k[0] === "admin" && k[1] === "tournament") ||
              (k[0] === "public" && k[1] === "tournament")
            );
          },
        });
      }
    };

    // Subscribe to the multiplexed `notifications` channel. Server dispatch
    // per-user_id (hub_bus.broadcast_notifications), only matching sockets
    // receive.
    const unsub = wsSubscribe("notifications", (msg) => {
      if (msg?.type === "notification") onPush(msg);
    });

    // Diagnostic trail — chỉ log open, hook không quản socket trực tiếp.
    import("../lib/refereeNavDiag")
      .then((m) => m.recordDiagEvent("ws_hub_notifications_subscribed"))
      .catch(() => {});

    return () => {
      unsub();
    };
  }, [enabled, userId, qc, navigate]);

  return {
    items: query.data?.items || [],
    unreadCount: query.data?.unread_count || 0,
    isLoading: query.isLoading,
    refetch: query.refetch,
    markRead: (id) => markReadMut.mutate(id),
    markAllRead: () => markAllReadMut.mutate(),
  };
}
